import React from 'react';
import { BusinessPlan, SectionId } from '../../types/businessPlan';
import { BusinessIdea } from '../../services/businessIdeaTypes';

interface GenerationProgressProps {
  idea: BusinessIdea | null;
  businessPlan?: BusinessPlan;
  currentSection?: SectionId;
}

export const GenerationProgress: React.FC<GenerationProgressProps> = ({ idea, businessPlan, currentSection }) => {
  // Define steps in the generation process
  const generationSteps = [
    'Analyzing business idea',
    'Researching market data',
    'Creating executive summary',
    'Developing financial projections',
    'Finalizing business plan'
  ];
  
  // Simulate progress based on time (in a real app, this would be based on actual progress)
  const [currentStep, setCurrentStep] = React.useState(0);
  const [progress, setProgress] = React.useState(0);
  
  React.useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 1;
      });
      
      if (progress >= 95 && currentStep < generationSteps.length - 1) {
        setCurrentStep(prev => prev + 1);
        setProgress(0);
      }
    }, 100);
    
    return () => clearInterval(interval);
  }, [progress, currentStep, generationSteps.length]);
  
  const overallProgress = ((currentStep * 100) + progress) / generationSteps.length;

  return (
    <div className="mt-4 p-6 bg-white border border-gray-200 rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-gray-800">
          Generating Business Plan for {idea?.title || 'Your Idea'}
        </h3>
        <span className="text-sm font-medium text-blue-600">
          {Math.round(overallProgress)}% Complete
        </span>
      </div>
      
      <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6">
        <div
          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
          style={{ width: `${overallProgress}%` }}
        ></div>
      </div>

      <div className="space-y-4">
        {generationSteps.map((step, index) => (
          <div key={index} className="flex items-center">
            <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full mr-3 
              ${index < currentStep ? 'bg-green-100 text-green-500' : 
                index === currentStep ? 'bg-blue-100 text-blue-500' : 
                'bg-gray-100 text-gray-400'}`}>
              {index < currentStep ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                </svg>
              ) : (
                <span className="text-sm font-medium">{index + 1}</span>
              )}
            </div>
            <div className="flex-grow">
              <p className={`text-sm font-medium 
                ${index < currentStep ? 'text-green-600' : 
                  index === currentStep ? 'text-blue-600' : 
                  'text-gray-500'}`}>
                {step}
              </p>
              {index === currentStep && (
                <div className="w-full bg-blue-100 rounded-full h-1.5 mt-1">
                  <div
                    className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      <p className="mt-6 text-sm text-gray-500">
        Please wait while we generate your business plan. This may take a few minutes.
      </p>
    </div>
  );
};
